import * as THREE from 'three';
import {
  ARRIVAL_METRES,
  inFrontOf,
  pinchScale,
  spanBetween,
  type Point,
} from '../engine/placement.js';
import type { Hands } from './hands.js';
import type { Stage } from './stage.js';

/**
 * Something in an AR lesson a student can put where they want it -- and then
 * lock, so it stays there.
 *
 * A lesson placed at the room's origin stands inside whoever started the
 * session, so it arrives in front of the viewer. After that it is theirs:
 *
 * - **Two hands, pinched** resize it, if it is resizable: apart is bigger,
 *   together is smaller, following the point between the hands.
 * - **Moving** is done by its handles (handles.ts), not here. One hand
 *   pinched inside it used to carry it, which meant walking up to a skeleton
 *   1.6 m away before it could be brought any closer.
 *
 * **Locked by default.** Moving was the first thing built and the first thing
 * that got in the way: a child reaching for a bone and missing it by a few
 * centimetres picked up the whole skeleton instead, "half the time" in
 * Pieter's words. A student who wants to move it unlocks it with the padlock
 * beside it (LockButton), puts it down, and locks it again. What stays put is
 * the thing being built on.
 *
 * Works for an object inside another: the tray lives in the skeleton's own
 * space so a skeleton shrunk onto a desk gets a desk-sized tray of desk-sized
 * bones. So positions are converted to the object's parent, not the world.
 *
 * VR lessons do not use this: there the scene *is* the room, and a solar
 * system a student could pick up and shrink would be a toy rather than a
 * place to stand.
 */
export class Placeable {
  /** Starts locked. See the class comment for why. */
  locked = true;

  #resizing:
    | { startSpan: number; startScale: number; startMid: THREE.Vector3; hands: [number, number] }
    | undefined;
  #startPosition = new THREE.Vector3();

  readonly #object: THREE.Object3D;
  readonly #stage: Stage;
  readonly #resizable: boolean;
  readonly #bounds: () => THREE.Box3;

  constructor(
    object: THREE.Object3D,
    stage: Stage,
    options: {
      /** Two hands may resize it. The skeleton yes; the tray follows the skeleton's size. */
      resizable?: boolean;
      /**
       * What counts as "inside it" for a one-handed grab, in world space.
       * Defaults to everything drawn. The skeleton passes its own outline, so
       * the tray in front of it -- which lives inside its space -- is not a
       * handle for the whole body.
       */
      bounds?: () => THREE.Box3;
    } = {},
  ) {
    this.#object = object;
    this.#stage = stage;
    this.#resizable = options.resizable ?? true;
    this.#bounds = options.bounds ?? ((): THREE.Box3 => new THREE.Box3().setFromObject(object));
  }

  /**
   * Puts it in front of the viewer, standing on the floor and facing them.
   *
   * Allowed while locked: arriving is the lesson placing itself, not a
   * student moving it.
   */
  arrive(): void {
    const camera = this.#stage.activeCamera;
    const head = new THREE.Vector3();
    camera.getWorldPosition(head);

    const facing = new THREE.Euler().setFromQuaternion(
      camera.getWorldQuaternion(new THREE.Quaternion()),
      'YXZ',
    );
    const at = inFrontOf({ x: head.x, z: head.z, yaw: facing.y }, ARRIVAL_METRES);

    this.#object.position.set(at.x, 0, at.z);
    this.#object.rotation.y = at.yaw;
  }

  /**
   * One frame of hands. Returns the hands it is using, so whatever runs after
   * it -- the skeleton, after the tray -- leaves them alone.
   */
  update(hands: Hands, busy: ReadonlySet<number> = new Set()): ReadonlySet<number> {
    const used = new Set<number>();

    if (this.locked) {
      this.#resizing = undefined;
      return used;
    }

    // A hand holding a bone, or moving the tray, is not a hand moving this.
    const pinching = hands.states
      .map((state, index) => ({ state, index }))
      .filter(({ state, index }) => state.tracked && state.pinching && !busy.has(index));

    if (this.#resizable && pinching.length >= 2) {
      const [a, b] = [pinching[0]!, pinching[1]!];
      this.#resize(a.state.pinchPoint, b.state.pinchPoint, [a.index, b.index]);
      if (this.#resizing) this.#resizing.hands.forEach((hand) => used.add(hand));
      return used;
    }

    this.#resizing = undefined;

    // No one-handed carrying any more. It meant pinching inside the thing, so
    // a student had to walk up to a skeleton 1.6 m away before they could pull
    // it closer. The move handle beside it does that from where they stand
    // (handles.ts); two hands here still resize.

    return used;
  }

  /** Where a world point is, in the space this object is positioned in. */
  #toParent(world: THREE.Vector3): THREE.Vector3 {
    return this.#object.parent ? this.#object.parent.worldToLocal(world.clone()) : world.clone();
  }

  #resize(a: THREE.Vector3, b: THREE.Vector3, hands: [number, number]): void {
    const mid = new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5);
    const span = spanBetween(toPoint(a), toPoint(b));

    if (!this.#resizing) {
      // Two hands must both start on it, or reaching for a bone with each
      // hand becomes a resize.
      const box = this.#bounds().expandByScalar(0.04);
      if (!box.containsPoint(a) || !box.containsPoint(b)) return;

      this.#resizing = {
        startSpan: span,
        startScale: this.#object.scale.x,
        startMid: mid.clone(),
        hands,
      };
      this.#startPosition.copy(this.#object.position);
      return;
    }

    const scale = pinchScale({
      startSpan: this.#resizing.startSpan,
      span,
      startScale: this.#resizing.startScale,
    });
    this.#object.scale.setScalar(scale);

    // Follows the point between the hands, so resizing does not also shove it
    // across the room.
    const moved = this.#toParent(mid).sub(this.#toParent(this.#resizing.startMid));
    this.#object.position.copy(this.#startPosition).add(moved);
    this.#object.position.y = Math.max(0, this.#object.position.y);
  }
}

function toPoint(v: THREE.Vector3): Point {
  return [v.x, v.y, v.z];
}
